import { useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Breadcrumb } from '@hivebrite/design-system/Breadcrumb';
import { Card } from '@hivebrite/design-system/Card';
import { Container } from '@hivebrite/design-system/Container';
import { Skeleton } from '@hivebrite/design-system/Skeleton';
import {
  useBreakpoint,
  useMatchBreakpoint,
} from '@hivebrite/design-system/Theme';

import { useDisableBottomLinksAccordion } from 'hb-react/frontoffice/components/BottomLinksAccordion/hooks';
import { Sidebar } from 'hb-react/shared/apps/Events/pages/Booking/components/Layout/components';
import { CartNotFound } from 'hb-react/shared/apps/Events/pages/Booking/components/Layout/components/CartNotFound';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import {
  Col,
  Grid,
  Row,
  getBpNum,
} from 'hb-react/shared/components/DesignSystem/Grid';
import RequiredExplanation from 'hb-react/shared/components/formik/components/RequiredExplanation';
import type { TicketCartItem } from 'hb-react/shared/components/ShoppingCard/hooks/v2';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import { Card as SidebarCard } from './components/Card';

export const Layout = () => {
  const outboxedFontTheme = useOutboxedFontTheme();
  const { flagEvOrderSummaryDsEvents } = useFeatureFlag();
  const breakpoint = useBreakpoint();

  const isMobile = useMatchBreakpoint(['s', 'm']);
  const navigate = useNavigate();

  useDisableBottomLinksAccordion();

  const { event, page, cart, isLoading } = useBooking();

  const breadcrumbItems = useMemo(() => {
    const tickets = (cart?.cart_items || []).filter(
      (item): item is TicketCartItem =>
        'line_itemable_type' in item &&
        item.line_itemable_type === 'NetworkEvents::Ticket',
    );

    const isRegistrationPageValid =
      page !== 'success' &&
      tickets.length > 0 &&
      tickets.every(
        (attendee) =>
          attendee?.metadata?.event?.firstname &&
          attendee?.metadata?.event?.lastname &&
          attendee?.metadata?.event?.email,
      );

    const pages = [
      'ticket-selection',
      'registration',
      'billing',
      'payment',
      'success',
    ];

    const pageIdx = pages.findIndex((p) => p === page);

    return [
      {
        label: __('Ticket selection'),
        onClickAction: () => navigate('./ticket-selection'),
        active: pageIdx === 0,
        disable: false,
      },
      {
        label: __('Registration'),
        onClickAction: () => navigate('./registration'),
        active: pageIdx === 1,
        disable: pageIdx < 1,
      },
      {
        label: __('Billing Information'),
        onClickAction: () => navigate('./billing'),
        active: pageIdx === 2,
        disable: !isRegistrationPageValid || pageIdx < 2,
      },
      {
        label: __('Payment'),
        active: pageIdx === 3,
        disable: pageIdx < 3,
      },
    ];
  }, [cart?.cart_items, navigate, page]);

  if (!event) {
    return <LayoutSkeleton />;
  }

  const requiredExplanation = ['registration', 'billing'].includes(page);

  return (
    <Container height="auto">
      <Box display="block" width="100%" mt={4} pb={{ _: 95, l: 80 }}>
        <Grid rowGap={0}>
          {page !== 'success' && (
            <Row>
              <LeftColumn>
                <ThemeProvider
                  theme={outboxedFontTheme.themeName}
                  fontBrightness={outboxedFontTheme.fontBrightness}
                >
                  <Box mb={4} id="steps">
                    {breakpoint === 's' ? (
                      <Box font="h2-semibold">
                        {breadcrumbItems.find(({ active }) => active)?.label}
                      </Box>
                    ) : (
                      <Breadcrumb
                        tailing={false}
                        breadcrumbItems={breadcrumbItems}
                      />
                    )}{' '}
                  </Box>
                  {requiredExplanation && !isMobile && (
                    <Box mt={{ _: 0, l: 3 }} mb={4}>
                      <RequiredExplanation />
                    </Box>
                  )}
                </ThemeProvider>
              </LeftColumn>
            </Row>
          )}
          {flagEvOrderSummaryDsEvents &&
            ['s', 'm'].includes(breakpoint) &&
            page !== 'payment' && (
              <Row>
                <LeftColumn>
                  <Box
                    flexDirection="column"
                    mb={
                      ['l', 'xl'].includes(breakpoint) ||
                      page === 'ticket-selection'
                        ? 6
                        : 0
                    }
                  >
                    {event ? <SidebarCard event={event} /> : <Skeleton />}
                  </Box>
                </LeftColumn>
              </Row>
            )}
          <Row>
            <LeftColumn>
              {requiredExplanation && isMobile && (
                <ThemeProvider
                  theme={outboxedFontTheme.themeName}
                  fontBrightness={outboxedFontTheme.fontBrightness}
                >
                  <Box
                    mt={{ _: flagEvOrderSummaryDsEvents ? 4 : 0, l: 3 }}
                    mb={{ _: 4, l: 0 }}
                  >
                    <RequiredExplanation />
                  </Box>
                </ThemeProvider>
              )}
              <Box display="block">
                {!isLoading && !cart ? <CartNotFound /> : <Outlet />}
              </Box>
            </LeftColumn>
            <Col
              width={({ numCols }) => ({
                _: 1,
                l: flagEvOrderSummaryDsEvents
                  ? 5 / getBpNum(numCols, 'xl')
                  : 4 / getBpNum(numCols, 'xl'),
                xl: flagEvOrderSummaryDsEvents
                  ? 4 / getBpNum(numCols, 'xl')
                  : 3 / getBpNum(numCols, 'xl'),
              })}
              offset={({ numCols }) => ({
                _: 0,
                l: flagEvOrderSummaryDsEvents ? 0 : 1 / getBpNum(numCols, 'xl'),
              })}
              order={{
                _: !flagEvOrderSummaryDsEvents || page === 'payment' ? 1 : 2,
                l: 2,
              }}
              mb={{ _: 5, l: 0 }}
            >
              <Sidebar page={page} />
            </Col>
          </Row>
        </Grid>
      </Box>
    </Container>
  );
};

export const LayoutSkeleton = () => {
  const { flagEvOrderSummaryDsEvents } = useFeatureFlag();

  return (
    <Container height="auto">
      <Box display="block" width="100%" mt={4} pb={{ _: 95, l: 80 }}>
        <Grid rowGap={0}>
          <Row>
            <LeftColumn>
              <Box mb={4}>
                <Skeleton height="30px" />
              </Box>
            </LeftColumn>
          </Row>
          <Row>
            <LeftColumn>
              <Box display="block">
                <Card>
                  <Skeleton mb={4} />

                  <Skeleton mb={2} />

                  <Skeleton mb={1} />
                  <Skeleton mb={1} />
                  <Skeleton mb={1} />
                </Card>
              </Box>

              <Box justifyContent="space-between" mt={4}>
                <Box>
                  <Skeleton mb={1} width="100px" height="32px" />
                </Box>
                <Box ml="auto">
                  <Skeleton mb={1} width="100px" height="32px" />
                </Box>
              </Box>
            </LeftColumn>
            <Col
              width={({ numCols }) => ({
                _: 1,
                l: flagEvOrderSummaryDsEvents
                  ? 5 / getBpNum(numCols, 'xl')
                  : 4 / getBpNum(numCols, 'xl'),
                xl: flagEvOrderSummaryDsEvents
                  ? 4 / getBpNum(numCols, 'xl')
                  : 3 / getBpNum(numCols, 'xl'),
              })}
              offset={({ numCols }) => ({
                _: 0,
                l: flagEvOrderSummaryDsEvents ? 0 : 1 / getBpNum(numCols, 'xl'),
              })}
              order={{ _: 1, l: 2 }}
              mb={{ _: 5, l: 0 }}
            >
              <Card>
                <Skeleton mb={4} height="175px" />
                <Skeleton mb={1} />
                <Skeleton mb={1} />
                <Skeleton mb={1} />
              </Card>
            </Col>
          </Row>
        </Grid>
      </Box>
    </Container>
  );
};

const LeftColumn = styled(Col).attrs<typeof Col>({
  width: ({ numCols }) => ({
    _: 1,
    l: 7 / Number(numCols),
    xl: 6 / Number(numCols),
  }),
  offset: ({ numCols }) => ({
    _: 0,
    xl: 1 / Number(numCols),
  }),
  order: { _: 2, l: 1 },
})``;
